import React, { useState } from 'react';
import styles from './OnboardingStep.module.css';
import Layout from '../layout/Layout';
import ActionButton from '../button/ActionButton';
import SecondaryButton from '../button/SecondaryButton';

export interface OnboardingContext {
  stepNumber: number;
  totalSteps: number;
  onBack?: () => void;
  loading?: boolean;
  removeTopPadding?: boolean;
}

interface OnboardingStepProps {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  onboardingContext: OnboardingContext;
  buttonEnabled: boolean;
  onContinue: (setLoading: (loading: boolean) => void) => void;
  hasDataLoad?: boolean;
  onSkip?: () => void;
  additionalButtons?: AdditionalButton[]; // New prop for additional callbacks
  primaryButtonText?: string;
  secondaryButtonText?: string;
}

export interface AdditionalButton {
  label: string;
  onClick: () => void;
  type: 'primary' | 'secondary'; // Determine the button style
  iconType?: 'Google' | 'Apple';
  hasDataLoad?: boolean;
  className?: string;
}

const OnboardingStep: React.FC<OnboardingStepProps> = ({
  title,
  subtitle,
  children,
  onboardingContext,
  buttonEnabled,
  onContinue,
  onSkip,
  hasDataLoad,
  additionalButtons = [],
  primaryButtonText,
  secondaryButtonText,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const actionButton =
    <ActionButton onClick={() => {
      setLoading(true);
      onContinue(setLoading);
    }} disabled={!buttonEnabled} hasDataLoad={hasDataLoad} hasLoadingSignal={!hasDataLoad && loading}>
      {primaryButtonText || 'Continue'}
    </ActionButton>;

  const skipButton =
    onSkip ?
      <SecondaryButton onClick={onSkip}>
        {secondaryButtonText || 'Skip for now'}
      </SecondaryButton>
      : <div />;

  const additionalButtonElements = additionalButtons.map((button, index) =>
    button.type === 'primary' ? (
      <ActionButton
        key={index}
        onClick={button.onClick}
        iconType={button.iconType}
        hasDataLoad={button.hasDataLoad}
        hasLoadingSignal={onboardingContext.loading}
        className={button.className}
      >
        {button.label}
      </ActionButton>
    ) : (
      <SecondaryButton key={index} onClick={button.onClick}>
        {button.label}
      </SecondaryButton>
    )
  );

  const footer = (
    <>
      {additionalButtonElements}
      {actionButton}
      {skipButton}
    </>
  );

  return (
    <Layout navbarConfig={{ title: "Sign Up", onBack: onboardingContext.onBack }}
      footerConfig={{
        fixed: true
      }}
      footer={footer}>
      <div className={styles.onboardingStepContainer}>
        <div className={styles.onboardingStepContentGroup}>
          <div className={styles.onboardingStepProgress}>
            {Array.from({ length: onboardingContext.totalSteps }).map((_, index) => (
              <span
                key={index}
                className={`${index + 1 === onboardingContext.stepNumber ? styles.activeDot : styles.dot} ${index + 1 === onboardingContext.stepNumber && index === 0 ? styles.firstDot : ''}`}
              >
                {index + 1 === onboardingContext.stepNumber ? index + 1 : ''}
              </span>
            ))}

          </div>
          <div className={styles.onboardingOverflowContainer}>
            <div className={styles.onboardingStepTitle}>{title}</div>
            <div className={styles.onboardingStepSubTitle}>{subtitle}</div>
            <div className={styles.onboardingStepContent}>{children}</div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OnboardingStep;
